import * as React from "react";
import { Link } from "react-router-dom";
import type { Lang } from "./Landing.js";
import { allCompetitors, getCompetitor, type Competitor, type SourceRef, type Sourced, type Verdict } from "./comparisons.js";

type L = Record<Lang, string>;

const UI = {
  hubTitle: { "pt-BR": "Klapzo vs. as alternativas", "en-US": "Klapzo vs. the alternatives", "es-LATAM": "Klapzo vs. las alternativas" } as L,
  hubLead: {
    "pt-BR": "Muita gente empilha uma ferramenta de engajamento (Mentimeter, Kahoot, Slido) com uma de planejamento (SessionLab). O Klapzo reúne as duas coisas, em português. Os dados de cada concorrente trazem fonte pública e data de leitura.",
    "en-US": "Many teams stack an engagement tool (Mentimeter, Kahoot, Slido) with a planning one (SessionLab). Klapzo brings both together, in Portuguese. Each competitor data point carries a public source and the date it was read.",
    "es-LATAM": "Muchos equipos apilan una herramienta de interacción (Mentimeter, Kahoot, Slido) con una de planificación (SessionLab). Klapzo reúne ambas, en portugués. Cada dato de la competencia incluye fuente pública y fecha de lectura.",
  } as L,
  see: { "pt-BR": "Ver comparação", "en-US": "See comparison", "es-LATAM": "Ver comparación" } as L,
  vs: { "pt-BR": "vs", "en-US": "vs", "es-LATAM": "vs" } as L,
  feature: { "pt-BR": "Recurso", "en-US": "Feature", "es-LATAM": "Función" } as L,
  whatIs: { "pt-BR": "O que é", "en-US": "What it is", "es-LATAM": "Qué es" } as L,
  whenFits: { "pt-BR": "Quando o {name} é a melhor escolha", "en-US": "When {name} is the better choice", "es-LATAM": "Cuándo {name} es la mejor opción" } as L,
  freeTier: { "pt-BR": "Plano grátis", "en-US": "Free tier", "es-LATAM": "Plan gratis" } as L,
  pricing: { "pt-BR": "Preço", "en-US": "Pricing", "es-LATAM": "Precio" } as L,
  unverified: { "pt-BR": "não verificado", "en-US": "not verified", "es-LATAM": "no verificado" } as L,
  source: { "pt-BR": "Fonte", "en-US": "Source", "es-LATAM": "Fuente" } as L,
  readOn: { "pt-BR": "lido em", "en-US": "read on", "es-LATAM": "leído el" } as L,
  partial: { "pt-BR": "parcial", "en-US": "partial", "es-LATAM": "parcial" } as L,
  ctaTitle: { "pt-BR": "Engajamento e planejamento, num só lugar.", "en-US": "Engagement and planning, in one place.", "es-LATAM": "Interacción y planificación, en un solo lugar." } as L,
  cta: { "pt-BR": "Experimentar o Klapzo", "en-US": "Try Klapzo", "es-LATAM": "Probar Klapzo" } as L,
  back: { "pt-BR": "← Todas as comparações", "en-US": "← All comparisons", "es-LATAM": "← Todas las comparaciones" } as L,
  disclaimer: {
    "pt-BR": "Cada dado de concorrente mostra a fonte pública e a data de leitura; o que não conseguimos verificar aparece como “não verificado”. Planos, limites e preços mudam: confira no site oficial de cada ferramenta. Marcas citadas pertencem aos seus respectivos donos e aparecem só para comparação factual.",
    "en-US": "Each competitor data point shows its public source and the date it was read; anything we could not verify is shown as “not verified”. Plans, limits and prices change: check each tool's official site. Trademarks belong to their respective owners and appear only for factual comparison.",
    "es-LATAM": "Cada dato de la competencia muestra su fuente pública y la fecha de lectura; lo que no pudimos verificar aparece como «no verificado». Planes, límites y precios cambian: consulta el sitio oficial de cada herramienta. Las marcas pertenecen a sus respectivos dueños y aparecen solo para comparación factual.",
  } as L,
};

/** Format an ISO date (YYYY-MM-DD) for the page locale. */
export function formatReadAt(iso: string, lang: Lang): string {
  const [y, m, d] = iso.split("-");
  return lang === "en-US" ? `${y}-${m}-${d}` : `${d}/${m}/${y}`;
}

function SourceNote({ source, lang }: { source: SourceRef; lang: Lang }): React.ReactElement {
  return (
    <div className="mt-1 text-xs text-muted">
      {UI.source[lang]}:{" "}
      <a href={source.url} target="_blank" rel="noopener noreferrer nofollow" className="underline">{source.url}</a>
      {" · "}{UI.readOn[lang]} {formatReadAt(source.readAt, lang)}
    </div>
  );
}

function FactCell({ fact, lang }: { fact: Sourced | null; lang: Lang }): React.ReactElement {
  if (!fact) return <div className="font-body text-muted">{UI.unverified[lang]}</div>;
  return (
    <div>
      <div className="font-body text-muted">{fact.text[lang]}</div>
      <SourceNote source={fact.source} lang={lang} />
    </div>
  );
}

function tpl(s: string, name: string): string {
  return s.replace("{name}", name);
}

/** Set <title> + description for SEO; restore on unmount. */
function useSeo(title: string, description: string): void {
  React.useEffect(() => {
    const prev = document.title;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? null;
    meta?.setAttribute("content", description);
    return () => {
      document.title = prev;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

function VerdictCell({ v, lang }: { v: Verdict | null; lang: Lang }): React.ReactElement {
  if (v === null) {
    return <span className="text-sm text-muted">{UI.unverified[lang]}</span>;
  }
  if (v === "yes") {
    return <span aria-label="sim" className="font-display text-foreground">✓</span>;
  }
  if (v === "partial") {
    return <span className="text-sm text-muted">{UI.partial[lang]}</span>;
  }
  return <span aria-label="não" className="text-muted">—</span>;
}

/** /comparar — hub listing every competitor comparison. */
export function ComparisonHub({ lang }: { lang: Lang }): React.ReactElement {
  useSeo(
    `Klapzo vs. Mentimeter, Kahoot, Slido e SessionLab — comparação`,
    UI.hubLead[lang],
  );
  return (
    <main className="mx-auto max-w-5xl px-5 py-16">
      <h1 className="font-display text-display-lg">{UI.hubTitle[lang]}</h1>
      <p className="mt-4 max-w-prose font-body text-lg leading-normal text-muted">{UI.hubLead[lang]}</p>
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {allCompetitors().map((c) => (
          <Link
            key={c.slug}
            to={`/comparar/${c.slug}`}
            className="flex flex-col gap-2 rounded-md border border-border p-6 transition-colors hover:border-foreground"
          >
            <span className="font-display text-display-sm">
              Klapzo <span className="text-accent">{UI.vs[lang]}</span> {c.name}
            </span>
            <span className="text-sm text-muted">{c.category[lang]}</span>
            <span className="mt-2 font-body text-accent">{UI.see[lang]} →</span>
          </Link>
        ))}
      </div>
      <p className="mt-12 max-w-prose text-sm leading-normal text-muted">{UI.disclaimer[lang]}</p>
    </main>
  );
}

/** /comparar/:slug — a single Klapzo-vs-X page. */
export function ComparisonPage({ competitor, lang }: { competitor: Competitor; lang: Lang }): React.ReactElement {
  const name = competitor.name;
  useSeo(
    tpl(`Klapzo vs {name}: qual escolher? — comparação | Klapzo`, name),
    competitor.blurb[lang],
  );

  return (
    <main className="mx-auto max-w-4xl px-5 py-16">
      <Link to="/comparar" className="font-body text-sm text-accent">{UI.back[lang]}</Link>

      <h1 className="mt-4 font-display text-display-lg">
        Klapzo <span className="text-accent">{UI.vs[lang]}</span> {name}
      </h1>
      <p className="mt-2 font-body text-sm uppercase tracking-wide text-muted">{competitor.category[lang]}</p>

      {/* What it is */}
      <section className="mt-8 rounded-md bg-surface p-6">
        <h2 className="font-display text-display-sm">{UI.whatIs[lang]}: {name}</h2>
        <p className="mt-2 max-w-prose font-body leading-normal">{competitor.blurb[lang]}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-sm font-bold">{UI.freeTier[lang]}</div>
            <FactCell fact={competitor.freeTier} lang={lang} />
          </div>
          <div>
            <div className="text-sm font-bold">{UI.pricing[lang]}</div>
            <FactCell fact={competitor.pricing} lang={lang} />
          </div>
        </div>
      </section>

      {/* Feature matrix */}
      <section className="mt-10 overflow-hidden rounded-md border border-border">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border">
              <th className="p-4 font-body text-sm font-normal text-muted">{UI.feature[lang]}</th>
              <th className="bg-primary p-4 text-center font-display text-primary-foreground">Klapzo</th>
              <th className="p-4 text-center font-display">{name}</th>
            </tr>
          </thead>
          <tbody>
            {competitor.rows.map((r) => (
              <tr key={r.key} className="border-b border-border last:border-0">
                <td className="p-4 font-body text-sm">{r.label[lang]}</td>
                <td className="bg-surface p-4 text-center text-lg"><VerdictCell v={r.klapzo} lang={lang} /></td>
                <td className="p-4 text-center text-lg"><VerdictCell v={r.them} lang={lang} />{r.source && <SourceNote source={r.source} lang={lang} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Honest "when they fit" — credibility */}
      <section className="mt-10 border-l-4 border-accent pl-5">
        <h2 className="font-display text-display-sm">{tpl(UI.whenFits[lang], name)}</h2>
        <p className="mt-2 max-w-prose font-body leading-normal text-muted">{competitor.whenItFits[lang]}</p>
      </section>

      {/* CTA */}
      <section className="mt-12 rounded-md bg-brand-black p-8 text-center">
        <h2 className="font-display text-display-md text-white">{UI.ctaTitle[lang]}</h2>
        <Link
          to="/entrar"
          className="mt-5 inline-block rounded-md bg-primary px-7 py-3 font-display text-primary-foreground"
        >
          {UI.cta[lang]}
        </Link>
      </section>

      <p className="mt-10 max-w-prose text-sm leading-normal text-muted">{UI.disclaimer[lang]}</p>
    </main>
  );
}

export { getCompetitor };
